import React, { useCallback, useEffect, useState } from 'react';
import { apiGet } from '../../../services/api';
import './ProcedureHistory.css';

interface HistoryItem {
    id: number;
    visit_id: number;
    patient_id: string;
    patient_name: string;
    patient_mobile: string;
    age: number;
    gender: string;
    tooth_no: string;
    procedure_type: string;
    status: string;
    consultant: string;
    created_at: string;
    notes?: string;
}

interface Props {
    procedureType: string; // e.g., 'RCT', 'Extraction', 'Implant'
    title?: string;
    status?: string;
}

export default function ProcedureHistory({ procedureType, title, status }: Props) {
    const [items, setItems] = useState<HistoryItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [limit, setLimit] = useState(50);

    const fetchHistory = useCallback(() => {
        setLoading(true);
        apiGet(`/api/procedures/history?procedure_type=${encodeURIComponent(procedureType)}&limit=${limit}${status ? `&status=${encodeURIComponent(status)}` : ""}`)
            .then((data: any) => setItems(data || []))
            .catch(err => console.error("History fetch error:", err))
            .finally(() => setLoading(false));
    }, [procedureType, status, limit]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    return (
        <div className="procedure-history-container" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 8px' }}>
                <h3 style={{ margin: 0, color: 'var(--mod-text-main)', fontSize: '1.1rem' }}>📜 {title || `${procedureType} History`}</h3>
                <button className="btn-refresh" onClick={fetchHistory} style={{ padding: '6px 12px', borderRadius: 'var(--mod-radius-pill)', border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', color: 'var(--mod-text-main)', fontSize: '0.85rem', fontWeight: 600 }}>↻ Refresh</button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {loading ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--mod-text-muted)' }}>Loading history...</div>
                ) : items.length === 0 ? (
                    <div className="mod-empty-state" style={{ padding: '30px', border: '1px dashed #cbd5e1', background: 'transparent' }}>
                         <p style={{ margin: 0 }}>No history records found.</p>
                    </div>
                ) : (
                    items.map(item => (
                        <div key={item.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', boxShadow: 'var(--mod-shadow-sm)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <strong style={{ fontSize: '1rem', color: 'var(--mod-text-main)' }}>{item.patient_name}</strong>
                                <span style={{
                                    background: item.status === 'Completed' ? '#dcfce7' : '#f3f4f6',
                                    color: item.status === 'Completed' ? '#166534' : '#374151',
                                    padding: '4px 8px',
                                    borderRadius: '6px',
                                    fontSize: '0.75rem',
                                    fontWeight: 700
                                }}>
                                    {item.status}
                                </span>
                            </div>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.85rem', color: 'var(--mod-text-muted)', marginBottom: '12px' }}>
                                <div>
                                    📅 {new Date(item.created_at).toLocaleDateString()}
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    🦷 {item.tooth_no ? `Tooth ${item.tooth_no}` : 'N/A'}
                                </div>
                                <div>
                                    {item.age}Y • {item.gender}
                                </div>
                                <div style={{ textAlign: 'right', color: 'var(--mod-sky)', fontWeight: 600 }}>
                                    📞 {item.patient_mobile}
                                </div>
                            </div>
                            
                            {(item.consultant || item.notes) && (
                                <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '10px', marginTop: '4px', fontSize: '0.85rem', color: '#64748b' }}>
                                    {item.consultant && <div style={{ marginBottom: '4px' }}>👨‍⚕️ Dr. {item.consultant}</div>}
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            {items.length >= limit && (
                <div style={{ padding: '16px 8px', textAlign: 'center' }}>
                    <button onClick={() => setLimit(l => l + 50)} style={{ padding: '8px 16px', background: '#f8fafc', color: 'var(--mod-text-main)', border: '1px solid #cbd5e1', borderRadius: 'var(--mod-radius-pill)', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}>Load More Records</button>
                </div>
            )}
        </div>
    );
}
